import {
  DashboardWidget,
  useGroupSubscriberTooltipRender,
  useHintTooltipRender,
  useLoginReminderTooltipRender,
  usePianoTooltipRender,
} from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { useMemo } from "react";

import { accountAtom } from "scmp-app/lib/account";
import { page as HomePage } from "scmp-app/pages/home";

import { useAppPromotionMessageRender } from "./messages/app-promotion/hooks";
import { useUpdatePaymentMessageRender } from "./messages/update-payment/hooks";
import { Container, SignInText, StyledAnonymousAvatar, StyledAvatar } from "./styles";

type HomeHeaderAvatarProps = {
  avatarId?: string;
  className?: "";
};

export const HomeHeaderAvatar: FunctionComponent<HomeHeaderAvatarProps> = ({
  avatarId = "",
  ...props
}) => {
  const { hintTooltipRender } = useHintTooltipRender();
  const { pianoTooltipRender } = usePianoTooltipRender();
  const { loginReminderTooltipRender } = useLoginReminderTooltipRender();
  const { groupSubscriberTooltipRender } = useGroupSubscriberTooltipRender();
  const { isLoggedIn } = useAtomValue(accountAtom);
  const router = useRouter();

  const { pathname: homePagePathname } = HomePage.route({
    slide: "main",
  });

  const isHomePage = useMemo(
    () => router.pathname === homePagePathname,
    [router.pathname, homePagePathname],
  );

  const tooltipRenders = useMemo(
    () => [
      groupSubscriberTooltipRender,
      pianoTooltipRender,
      loginReminderTooltipRender,
      hintTooltipRender,
    ],
    [
      groupSubscriberTooltipRender,
      pianoTooltipRender,
      loginReminderTooltipRender,
      hintTooltipRender,
    ],
  );

  const { updatePaymentMessageRender } = useUpdatePaymentMessageRender();
  const { appPromotionMessageRender } = useAppPromotionMessageRender();
  const messageRenders = useMemo(
    () => [updatePaymentMessageRender, appPromotionMessageRender],
    [updatePaymentMessageRender, appPromotionMessageRender],
  );

  return (
    <Container $isLoggedIn={isLoggedIn}>
      <DashboardWidget
        messageRenders={messageRenders}
        tooltipRenders={isHomePage ? tooltipRenders : undefined}
        {...props}
      >
        {isLoggedIn ? (
          <StyledAvatar $isLoggedIn={isLoggedIn} id={avatarId} />
        ) : (
          <StyledAnonymousAvatar $isLoggedIn={isLoggedIn} />
        )}
        {!isLoggedIn && <SignInText>Sign in</SignInText>}
      </DashboardWidget>
    </Container>
  );
};

HomeHeaderAvatar.displayName = "HomeHeaderAvatar";
